import { useQuery } from '@tanstack/react-query';
import authApi from '@/services/api/authApi';
import type { User } from '@/types/api';
import { isAuthSessionError, isTransientAuthBootstrapError } from '@/utils/authBootstrapErrors';
import { safeGetItem, safeSetItem } from '@/utils/safeStorage';

/** Shared query key for current user – used by useAuth for invalidation */
export const currentUserQueryKey = ['users', 'current'] as const;

export function useCurrentUserQuery() {
  return useQuery({
    queryKey: currentUserQueryKey,
    queryFn: async (): Promise<User | null> => {
      const fullName = safeGetItem('fullName');
      const userId = safeGetItem('userId');
      const companyId = safeGetItem('companyId');
      if (!fullName || !userId || !companyId) {
        authApi.clearUserInfo();
        return null;
      }
      try {
        const user = await authApi.getCurrentUser();
        if (user?.companyId != null) {
          safeSetItem('companyId', String(user.companyId));
        }
        if (user?.role?.permissions) {
          authApi.setSessionPermissions(
            user.role.permissions
              .map((p) => p.name)
              .filter((name): name is string => typeof name === 'string' && name.length > 0),
          );
        }
        return user;
      } catch (error) {
        // Cold start / network / 5xx: keep local session and let React Query retry.
        if (isTransientAuthBootstrapError(error)) {
          throw error;
        }
        // 401/403 / session expired: clear and treat as logged out.
        if (isAuthSessionError(error)) {
          authApi.clearUserInfo();
          return null;
        }
        authApi.clearUserInfo();
        return null;
      }
    },
    retry: (failureCount, error) => {
      if (!isTransientAuthBootstrapError(error)) return false;
      return failureCount < 3;
    },
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 8000),
    gcTime: 1000 * 60 * 10,
    staleTime: 1000 * 60 * 2,
    refetchOnWindowFocus: true,
  });
}
